import React, { useCallback } from "react";

import { ProgressPoint } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/reducer/types";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import Complete from "./Icons/Complete";
import InProgress from "./Icons/InProgress";
import NotStarted from "./Icons/NotStarted";

import styles from "./WizardProgressBar.module.css";

interface Props {
  progressState: ProgressPoint[];
  className?: string;
  setPage?: (pageNumber: number) => void;
  canClickTo?: (idx: number) => boolean;
}

const WizardProgressBar = ({
  progressState,
  className,
  setPage,
  canClickTo,
}: Props): React.ReactElement => {
  const onCanClickTo = useCallback(
    (idx: number) => (canClickTo !== undefined ? canClickTo(idx) : false),
    [canClickTo]
  );

  const onSetPage = useCallback(
    (idx: number) =>
      setPage !== undefined ? () => setPage(idx) : () => undefined,
    [setPage]
  );

  return (
    <div className={toClassName(styles.container, className)}>
      {progressState.map((pp, idx, arr) => {
        return (
          <div
            key={pp.name}
            className={toClassName(styles.step, {
              [styles.noFlex]: idx === arr.length - 1,
            })}
          >
            <div
              className={toClassName(styles.flex, {
                [styles.clickArea]: onCanClickTo(idx),
              })}
              onClick={onSetPage(idx)}
            >
              <div className={styles.icon}>
                {pp.state === "Complete" ? (
                  <Complete />
                ) : pp.state === "InProgress" ? (
                  <InProgress />
                ) : (
                  <NotStarted />
                )}
              </div>

              <div
                className={toClassName(styles.text, {
                  [styles.notStarted]: pp.state === "NotStarted",
                  [styles.activeComplete]: pp.state !== "NotStarted",
                })}
              >{`${idx + 1}. ${pp.name}`}</div>
            </div>

            {idx < arr.length - 1 && (
              <div
                className={toClassName(styles.horizLine, {
                  [styles.notStartedLine]: pp.state === "NotStarted",
                  [styles.activeCompleteLine]: pp.state !== "NotStarted",
                })}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WizardProgressBar;
